import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined } from '@ant-design/icons';
import { Alert, Form as AntdForm } from 'antd';
import { routes } from '../../../../../utils/helpers/ApiClient';
import AppBarCMS from '../../../../basic/Navigation/AppBarCMS/AppBarCMS';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import { AdminContentBlock, Spinner } from '../../../../common';
import Form from '../../../../antd/form/Form/Form';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { applyPhdValidationErrors } from '../utils/phdApiFeedback.js';
import {
  useGetPhdThesisQuery,
  useSavePhdThesisMutation,
  useGetPhdSystemStateCurrentQuery,
  useLazyGetPhdThesisDocumentQuery,
} from '../../../../../redux/services/phd';
import { usePhdReferenceData } from '../usePhdReferenceData.jsx';
import PhdThesesEditForm from './PhdThesesEditForm.jsx';
import { downloadBlobResponse } from '../../../../../utils/helpers/downloadBlobResponse';
import useIsUserPhdAdmin from '../../../../../hooks/useIsUserPhdAdmin.jsx';
import { usePhdDashboardPrefill } from '../utils/usePhdDashboardPrefill.js';
import { usePhdDirtyLeaveGuard } from '../usePhdDirtyLeaveGuard.jsx';
import PhdArchivedRecordNotice from '../PhdArchivedRecordNotice.jsx';

const toDayjs = (value) => (value ? dayjs(value) : null);

const normalizeIdArray = (values) =>
  [...(values || [])]
    .filter((value) => value !== undefined && value !== null && value !== '')
    .map((value) => Number(value))
    .sort((a, b) => a - b);

const normalizeTextValue = (value) => (value === undefined ? null : value);

const normalizeFormValues = (values = {}) => ({
  phd_student_id: values.phd_student_id ?? null,
  phd_school_year_id: values.phd_school_year_id ?? null,
  phd_study_field_id: values.phd_study_field_id ?? null,
  phd_supervisor_id: values.phd_supervisor_id ?? null,
  phd_supervisor_specialist_id: values.phd_supervisor_specialist_id ?? null,
  title_sk: normalizeTextValue(values.title_sk),
  title_en: normalizeTextValue(values.title_en),
  description_sk: normalizeTextValue(values.description_sk),
  description_en: normalizeTextValue(values.description_en),
  scientific_contribution: normalizeTextValue(values.scientific_contribution),
  scientific_progress: normalizeTextValue(values.scientific_progress),
  research_type: normalizeTextValue(values.research_type),
  research_task: normalizeTextValue(values.research_task),
  solution_results: normalizeTextValue(values.solution_results),
  elective_subject_ids: normalizeIdArray(values.elective_subject_ids),
  is_daily_study: !!values.is_daily_study,
  is_external_study: !!values.is_external_study,
});

const buildInitialFormValues = (detail) => ({
  phd_student_id: detail?.phd_student_id,
  phd_school_year_id: detail?.phd_school_year_id,
  phd_study_field_id: detail?.phd_study_field_id,
  phd_supervisor_id: detail?.phd_supervisor_id,
  phd_supervisor_specialist_id: detail?.phd_supervisor_specialist_id,
  approved_at: toDayjs(detail?.approved_at || detail?.date_approved),
  title_sk: detail?.title_sk,
  title_en: detail?.title_en,
  description_sk: detail?.description_sk,
  description_en: detail?.description_en,
  scientific_contribution: detail?.scientific_contribution,
  scientific_progress: detail?.scientific_progress,
  research_type: detail?.research_type,
  research_task: detail?.research_task,
  solution_results: detail?.solution_results,
  elective_subject_ids: (detail?.elective_study_subjects || []).map(
    (subject) => subject.id,
  ),
  is_daily_study: !!detail?.is_daily_study,
  is_external_study: !!detail?.is_external_study,
});

const PhdThesesEdit = ({ mode }) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = AntdForm.useForm();
  const isAdmin = useIsUserPhdAdmin();
  const dashboardPrefill = usePhdDashboardPrefill();
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const [isSubmitted, setIsSubmitted] = useState(false);
  const pageTitle = isCreate
    ? t('Pridanie dizertačnej témy')
    : t('Editácia dizertačnej témy');

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  const { data: detail, isLoading } = useGetPhdThesisQuery(id, {
    skip: !isEdit,
  });
  const [saveThesis, { isLoading: isSaving }] = useSavePhdThesisMutation();
  const [downloadThesisDoc, { isLoading: isDownloading }] =
    useLazyGetPhdThesisDocumentQuery();
  const { saveSuccess, saveFailed } = useToastMessages();
  const { data: systemState, isLoading: isStateLoading } =
    useGetPhdSystemStateCurrentQuery();

  const {
    studentDisplayNameMap,
    schoolYearOptions,
    studyFieldOptions,
    examinerOptions,
    students,
    schoolYears,
    studyFields,
    requiredStudySubjectOptions,
    electiveStudySubjectOptions,
    ensureSchoolYearSelection,
    isCreatingSchoolYear,
    isLoading: isReferenceLoading,
  } = usePhdReferenceData({
    includeExaminers: true,
    includeStudySubjects: true,
  });

  const initials = useMemo(() => {
    if (isCreate) {
      return {
        ...buildInitialFormValues(null),
        ...dashboardPrefill,
      };
    }
    if (!detail) {
      return null;
    }
    return buildInitialFormValues(detail);
  }, [dashboardPrefill, detail, isCreate]);

  const normalizedInitials = useMemo(
    () => normalizeFormValues(initials || {}),
    [initials],
  );
  const { modal, onSaveFailed, onSaveSuccess, onValuesChange } =
    usePhdDirtyLeaveGuard(form, normalizedInitials);

  useEffect(() => {
    setIsSubmitted(false);
  }, [normalizedInitials]);

  const handleSubmit = async (values) => {
    setIsSubmitted(true);
    const isPublishedThesis =
      isEdit && Boolean(detail?.approved_at) && !detail?.archived_at;
    const payload = isPublishedThesis
      ? { phd_student_id: values.phd_student_id ?? null }
      : {
          phd_student_id: values.phd_student_id ?? null,
          phd_school_year_id: values.phd_school_year_id ?? null,
          phd_study_field_id: values.phd_study_field_id ?? null,
          phd_supervisor_id:
            values.phd_supervisor_id ?? detail?.phd_supervisor_id ?? null,
          phd_supervisor_specialist_id:
            values.phd_supervisor_specialist_id ?? null,
          title_sk: values.title_sk ?? null,
          title_en: values.title_en ?? null,
          description_sk: values.description_sk ?? null,
          description_en: values.description_en ?? null,
          scientific_contribution: values.scientific_contribution ?? null,
          scientific_progress: values.scientific_progress ?? null,
          research_type: values.research_type ?? null,
          research_task: values.research_task ?? null,
          solution_results: values.solution_results ?? null,
          elective_subject_ids: values.elective_subject_ids ?? [],
          is_daily_study: !!values.is_daily_study,
          is_external_study: !!values.is_external_study,
        };
    try {
      const savedThesis = await saveThesis({
        id: isEdit ? detail.id : undefined,
        body: payload,
      }).unwrap();
      saveSuccess();
      onSaveSuccess(() =>
        navigate(
          isCreate
            ? `${routes.cmsPhdThesesDetail}/${savedThesis.id}`
            : routes.cmsPhdTheses,
        ),
      );
    } catch (error) {
      onSaveFailed();
      setIsSubmitted(false);
      applyPhdValidationErrors(form, error);
      saveFailed(error);
    }
  };

  const handleDownload = async () => {
    if (!detail?.id) return;
    try {
      const response = await downloadThesisDoc(detail.id).unwrap();
      downloadBlobResponse(
        response,
        `${detail.title_sk || detail.title_en || t('Dizertačná téma')}.docx`,
      );
    } catch (error) {
      saveFailed(error);
    }
  };

  const mainActions = useMemo(() => {
    if (!isEdit || !detail) return [];
    return [
      {
        onClick: handleDownload,
        title: detail.is_frozen
          ? t('Stiahnuť DOCX')
          : t('Dokument ešte nemá zmrazenú archívnu verziu'),
        icon: <DownloadOutlined />,
        permission: 'phd-theses.index',
        disabled:
          isDownloading || !detail.is_frozen || detail.can_download === false,
      },
    ];
  }, [detail, handleDownload, isDownloading, isEdit, t]);

  if (
    (isEdit && isLoading) ||
    isReferenceLoading ||
    isStateLoading ||
    !initials
  ) {
    return <Spinner />;
  }

  const isSystemClosed = systemState && systemState.is_open === false;
  const isReadOnly = Boolean(detail?.archived_at || detail?.is_read_only);
  const isPending = isCreate || !detail?.approved_at;
  const isApproved = Boolean(detail?.approved_at);
  const isSupervisorHidden = isPending;
  const isSpecialistHidden = !isCreate && isPending;

  return (
    <>
      {!isSubmitted && !isReadOnly && modal}
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsPhdTheses}
        backUrlTooltip={t('Témy dizertačných prác')}
        permissionsBasic="phd-theses"
        mainActions={mainActions}
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako funguje vytvorenie a úprava témy?')}
          description={t(
            'Doplňte názvy, odbor, školiteľa a predmety témy. Nová téma čaká na posúdenie pracovnou skupinou; po schválení možno meniť iba priradeného doktoranda.',
          )}
        />
        <AdminContentBlock
          icon="fa fa-graduation-cap"
          link={routes.cmsPhdTheses}
          title={pageTitle}
        >
          <PhdArchivedRecordNotice record={detail} />
          {isSystemClosed && (
            <Alert
              type="warning"
              showIcon
              description={t(
                'Systém pre dizertačné témy je momentálne zatvorený. Nové témy nie je možné vytvárať ani upravovať, kým sa znovu neotvorí.',
              )}
              style={{ marginBottom: 16 }}
            />
          )}
          <Form
            form={form}
            initialValues={initials}
            onValuesChange={onValuesChange}
            onFinish={handleSubmit}
            disabled={isSystemClosed || isReadOnly}
          >
            <PhdThesesEditForm
              isAdmin={isAdmin}
              studentDisplayNameMap={studentDisplayNameMap}
              students={students}
              schoolYears={schoolYears}
              schoolYearOptions={schoolYearOptions}
              ensureSchoolYearSelection={ensureSchoolYearSelection}
              isCreatingSchoolYear={isCreatingSchoolYear}
              studyFieldOptions={studyFieldOptions}
              studyFields={studyFields}
              requiredStudySubjectOptions={requiredStudySubjectOptions}
              electiveStudySubjectOptions={electiveStudySubjectOptions}
              examinerOptions={examinerOptions}
              isStudentHidden={isPending}
              isSupervisorHidden={isSupervisorHidden}
              isSpecialistHidden={isSpecialistHidden}
              isApproved={isApproved}
              isSupervisorLocked={false}
              isSubmitting={isSystemClosed || isReadOnly ? false : isSaving}
              isSubmitDisabled={isSystemClosed || isReadOnly}
              isReadOnly={isReadOnly}
              currentThesisId={detail?.id}
              initialStudentId={detail?.phd_student_id}
            />
          </Form>
        </AdminContentBlock>
      </div>
    </>
  );
};

export default PhdThesesEdit;
